/**
 * L.Map.MarkerDisperse — a Leaflet Handler that coordinates marker
 * dispersal across all layers on the map, including markers in different
 * FeatureGroups and MarkerClusterGroups.
 *
 * Usage:
 *   const map = L.map('map');
 *   map.addHandler('markerdisperse', L.Map.MarkerDisperse);
 *   map.markerdisperse.enable();
 *
 *   // Or via map options:
 *   const map = L.map('map', { markerdisperse: true });
 *
 * Options can be passed via:
 *   map.options.markerDisperseOptions = { minimumSpacing: 2 };
 */

import L from 'leaflet';
import { disperse } from '../core/DisperseEngine.js';

// Default icon dimensions matching Leaflet's default marker icon
const DEFAULT_ICON_SIZE = [25, 41];
const DEFAULT_ICON_ANCHOR = [12, 41];

L.Map.MarkerDisperse = L.Handler.extend({
  initialize: function (map) {
    L.Handler.prototype.initialize.call(this, map);

    this._options = map.options.markerDisperseOptions || {};
    this._disperseTimeout = null;
    this._originalTransforms = {};
    this._trackedLayerIds = new Set();
  },

  addHooks: function () {
    this._map.on('zoomend', this._scheduleDisperse, this);
    this._map.on('moveend', this._scheduleDisperse, this);
    this._map.on('resize', this._scheduleDisperse, this);
    this._map.on('layeradd', this._scheduleDisperse, this);
    this._map.on('layerremove', this._onLayerRemove, this);

    this._scheduleDisperse();
  },

  removeHooks: function () {
    this._map.off('zoomend', this._scheduleDisperse, this);
    this._map.off('moveend', this._scheduleDisperse, this);
    this._map.off('resize', this._scheduleDisperse, this);
    this._map.off('layeradd', this._scheduleDisperse, this);
    this._map.off('layerremove', this._onLayerRemove, this);

    this._restoreAllTransforms();
  },

  /**
   * Update options at runtime and re-disperse.
   */
  setOptions: function (options) {
    Object.assign(this._options, options);
    this._scheduleDisperse();
  },

  /**
   * Force an immediate dispersal recalculation.
   */
  refreshDisperse: function () {
    this._performDisperse();
  },

  /**
   * @private
   */
  _onLayerRemove: function (e) {
    const id = L.Util.stamp(e.layer);
    if (this._trackedLayerIds.has(id)) {
      this._restoreTransform(id, e.layer);
      this._trackedLayerIds.delete(id);
    }
    this._scheduleDisperse();
  },

  /**
   * @private
   */
  _scheduleDisperse: function () {
    if (this._disperseTimeout) {
      clearTimeout(this._disperseTimeout);
    }
    this._disperseTimeout = setTimeout(() => {
      this._disperseTimeout = null;
      this._performDisperse();
    }, 50);
  },

  /**
   * @private
   */
  _performDisperse: function () {
    const map = this._map;
    if (!map) return;

    // Restore all transforms first
    this._restoreAllTransforms();

    const markers = [];
    const layerMap = {};

    // Collect all markers currently on the map, from all layers
    map.eachLayer((layer) => {
      this._collectMarkers(layer, markers, layerMap);
    });

    if (markers.length < 2) return;

    const offsets = disperse(markers, {
      minimumSpacing: this._options.minimumSpacing || 0,
      pattern: this._options.pattern || 'circle',
    });

    // Apply offsets
    for (const m of markers) {
      const offset = offsets[m.id];
      if (!offset || (offset.dx === 0 && offset.dy === 0)) continue;
      const layer = layerMap[m.id];
      if (layer) {
        this._applyTransform(m.id, layer, offset.dx, offset.dy);
        this._trackedLayerIds.add(m.id);
      }
    }
  },

  /**
   * Recursively collect markers from a layer and its children.
   * Handles FeatureGroups, LayerGroups, and MarkerClusterGroups.
   * @private
   */
  _collectMarkers: function (layer, markers, layerMap) {
    // If this is a MarkerClusterGroup, collect the visible cluster icons
    // and unclustered markers instead of recursing into the original markers.
    if (this._isMarkerClusterGroup(layer)) {
      this._collectClusterMarkers(layer, markers, layerMap);
      return;
    }

    // If this is a group, recurse
    if (layer.eachLayer && !(layer instanceof L.Marker)) {
      layer.eachLayer((child) => {
        this._collectMarkers(child, markers, layerMap);
      });
      return;
    }

    // If it can be "opened" (like a popup or tooltip), skip it.
    if (layer.openOn) {
      return;
    }

    // Otherwise, if it has a position and an element (like a marker)...
    if (layer.getLatLng && layer.getElement) {
      const map = this._map;
      const latlng = layer.getLatLng();
      const point = map.latLngToLayerPoint(latlng);
      const iconDims = this._getIconDimensions(layer);
      const id = L.Util.stamp(layer);

      // Skip the marker if it's already been added to the list.
      // This may happen because map.eachLayer() iterates over all layers,
      // including those that are grouped members of other layers.
      //
      // TODO: In that case, do we need to recurse into groups?
      if (layerMap[id]) {
        return;
      }

      markers.push({
        id,
        x: point.x,
        y: point.y,
        width: iconDims.width,
        height: iconDims.height,
        anchorX: iconDims.anchorX,
        anchorY: iconDims.anchorY,
      });
      layerMap[id] = layer;
    }
  },

  /**
   * Check whether a layer is a MarkerClusterGroup.
   * @private
   */
  _isMarkerClusterGroup: function (layer) {
    // Duck-type check for MarkerClusterGroup
    return !!(layer._topClusterLevel && layer._needsClustering !== undefined);
  },

  /**
   * Collect visible cluster icons and unclustered markers from a
   * MarkerClusterGroup.
   * @private
   */
  _collectClusterMarkers: function (clusterGroup, markers, layerMap) {
    // MarkerClusterGroup uses _featureGroup for visible layers
    const visibleParent = clusterGroup._featureGroup;
    if (!visibleParent) return;

    visibleParent.eachLayer((layer) => {
      if (layer.getLatLng && layer.getElement) {
        const map = this._map;
        const latlng = layer.getLatLng();
        const point = map.latLngToLayerPoint(latlng);
        const iconDims = this._getIconDimensions(layer);
        const id = L.Util.stamp(layer);

        markers.push({
          id,
          x: point.x,
          y: point.y,
          width: iconDims.width,
          height: iconDims.height,
          anchorX: iconDims.anchorX,
          anchorY: iconDims.anchorY,
        });
        layerMap[id] = layer;
      }
    });
  },

  _getIconDimensions: function (layer) {
    const icon = layer.options && layer.options.icon;
    const iconOpts = icon && icon.options;

    const defaultSize = (iconOpts && iconOpts.iconSize) || DEFAULT_ICON_SIZE;
    const defaultAnchor = (iconOpts && iconOpts.iconAnchor) || DEFAULT_ICON_ANCHOR;

    const origWidth = defaultSize.x !== undefined ? defaultSize.x : defaultSize[0];
    const origHeight = defaultSize.y !== undefined ? defaultSize.y : defaultSize[1];
    let anchorX = defaultAnchor.x !== undefined ? defaultAnchor.x : defaultAnchor[0];
    let anchorY = defaultAnchor.y !== undefined ? defaultAnchor.y : defaultAnchor[1];

    let width = origWidth;
    let height = origHeight;

    const override = this._options.markerSize;
    let sizeOverride = null;

    if (override) {
      if (typeof override === 'function') {
        sizeOverride = override(layer);
      } else {
        sizeOverride = override;
      }
    }

    if (sizeOverride) {
      width = sizeOverride.x !== undefined ? sizeOverride.x : sizeOverride[0];
      height = sizeOverride.y !== undefined ? sizeOverride.y : sizeOverride[1];

      if (origWidth > 0) anchorX = anchorX * (width / origWidth);
      if (origHeight > 0) anchorY = anchorY * (height / origHeight);
    }

    return { width, height, anchorX, anchorY };
  },

  /**
   * @private
   */
  _applyTransform: function (id, layer, dx, dy) {
    const el = layer.getElement ? layer.getElement() : null;
    if (!el) return;

    if (!(id in this._originalTransforms)) {
      this._originalTransforms[id] = el.style.transform || '';
    }

    // Set the target offset for this marker
    layer._disperseOffset = { dx, dy };

    // Duck-punch _setPos so that during zoom animation, the dispersed offset
    // is preserved on top of Leaflet's newly calculated base position.
    // This allows Leaflet 1.x and 2.x to smoothly transition the markers while
    // maintaining their dispersed visual layout.
    if (!layer._originalSetPos && typeof layer._setPos === 'function') {
      layer._originalSetPos = layer._setPos;
      layer._setPos = function (pos) {
        // Let Leaflet update the base position and set the transform
        // e.g. translate3d(newX, newY, 0)
        layer._originalSetPos.call(this, pos);

        // Then re-append our disperse offset to the new transform so it
        // smoothly animates.
        if (this._disperseOffset) {
          const el = this.getElement ? this.getElement() : null;
          if (el && el.style.transform !== undefined) {
             const original = el.style.transform || '';
             el.style.transform = original
               ? `${original} translate(${this._disperseOffset.dx}px, ${this._disperseOffset.dy}px)`
               : `translate(${this._disperseOffset.dx}px, ${this._disperseOffset.dy}px)`;
          }
        }
      };
    }

    const original = this._originalTransforms[id];
    el.style.transform = original
      ? `${original} translate(${dx}px, ${dy}px)`
      : `translate(${dx}px, ${dy}px)`;
  },

  /**
   * @private
   */
  _restoreTransform: function (id, layer) {
    if (id in this._originalTransforms) {
      // Clear the offset so if Leaflet updates position (or if the layer is
      // removed), it doesn't get our disperse offset appended.
      layer._disperseOffset = null;

      // Let Leaflet re-position the marker for the current zoom/pan state
      // instead of restoring from a cached transform that may be stale.
      if (layer.update) {
        layer.update();
      } else {
        const el = layer.getElement ? layer.getElement() : null;
        if (el) {
          el.style.transform = this._originalTransforms[id];
        }
      }

      // Restore original setPos to clean up duck-punching.
      if (layer._originalSetPos) {
        layer._setPos = layer._originalSetPos;
        delete layer._originalSetPos;
      }

      delete this._originalTransforms[id];
    }
  },

  /**
   * @private
   */
  _restoreAllTransforms: function () {
    const map = this._map;
    if (!map) return;

    // Walk all layers and restore tracked ones
    const restore = (layer) => {
      const id = L.Util.stamp(layer);
      if (this._trackedLayerIds.has(id)) {
        this._restoreTransform(id, layer);
        this._trackedLayerIds.delete(id);
      }
      if (layer.eachLayer) {
        layer.eachLayer(restore);
      }
      // Also check _featureGroup for MarkerClusterGroups
      if (layer._featureGroup) {
        layer._featureGroup.eachLayer((child) => {
          const childId = L.Util.stamp(child);
          if (this._trackedLayerIds.has(childId)) {
            this._restoreTransform(childId, child);
            this._trackedLayerIds.delete(childId);
          }
        });
      }
    };

    map.eachLayer(restore);
  },
});

// Register as a map handler so it can be enabled via map options
L.Map.addInitHook('addHandler', 'markerdisperse', L.Map.MarkerDisperse);

export default L.Map.MarkerDisperse;
